import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Flame, Zap, BookOpen, ArrowRight, Home, Star, RefreshCw, FlaskConical, Users, X } from 'lucide-react';
import { type ModuleData, type ModuleId, getFamilyDefinition, getFamiliesForModule } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG } from '../../config/constants';
import { useDjaySelection, getDjayGreeting, DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { AdventureCTA } from './AdventureCTA';
import { DjayRecommendationCard } from './DjayRecommendationCard';
import { WorldTile } from '../subjects/WorldTile';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { AssignedAssessmentBanner } from './AssignedAssessmentBanner';
import { dataLayer } from '../../data/dataLayer';
import type { AssessmentConfig } from '../../assessment';
import { DOMAIN_MODULE_MAP } from '../../assessment/utils/assessmentDomainConfig';
import { getWeeklyActivityForLearner, type DayActivity } from '../../utils/activityHelpers';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
import { hasActiveDecay } from '../../mastery/masteryDecay';
import { getWeakestFamiliesGlobal, getFamilyMasteryKey, getDjayRecommendation } from '../../mastery';
import { getEnabledModes } from '../../modes';
import { getModeIcon } from '../../modes/modeRegistry';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
  onNavigateToAssessment: () => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onNavigateToLab?: () => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onNavigateToAssessment,
  onStartAssessment,
  onNavigateToLab
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts,
    activeAccount
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const [activeAssignments, setActiveAssignments] = useState<any[]>([]);
  const [showAllModules, setShowAllModules] = useState(false);
  const [showKlascodeBanner, setShowKlascodeBanner] = useState(false);
  // Check if learner has no classroom and banner not dismissed
  useEffect(() => {
    async function checkKlascodeBanner() {
      if (!activeAccount || activeAccount.role !== 'learner') {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if banner was dismissed
      const dismissed = await dataLayer.getKlascodeBannerDismissed();
      if (dismissed) {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if learner is in any classroom
      const hasClassroom = accounts && Object.values(accounts.classrooms || {}).some(classroom => classroom.learnerIds.includes(activeAccount.id));
      setShowKlascodeBanner(!hasClassroom);
    }
    checkKlascodeBanner();
  }, [activeAccount, accounts]);
  const handleDismissKlascodeBanner = async () => {
    await dataLayer.setKlascodeBannerDismissed(true);
    setShowKlascodeBanner(false);
  };
  useEffect(() => {
    async function loadAssignments() {
      if (!activeAccount) return;
      const all = await dataLayer.getTeacherAssignments();
      const now = new Date();
      const valid = Object.values(all).filter((assignment: any) => {
        if (assignment.group !== selectedGroup) return false;
        if (assignment.deadline && new Date(assignment.deadline) < now) return false;
        const completedBy = assignment.completedByStudentIds as string[] || [];
        if (completedBy.includes(activeAccount.id)) return false;
        if (assignment.wholeClass) return true;
        if (assignment.targetStudentIds && assignment.targetStudentIds.includes(activeAccount.id)) return true;
        return false;
      });
      setActiveAssignments(valid);
    }
    loadAssignments();
  }, [activeAccount, selectedGroup]);
  const hour = new Date().getHours();
  let greeting = 'Hallo';
  if (hour < 8) greeting = 'Goedemorgen';else if (hour < 12) greeting = 'Goedemorgen';else if (hour < 18) greeting = 'Goedemiddag';else greeting = 'Goedenavond';
  const djayEmotion: DjayEmotion = useMemo(() => getDjayGreeting(displayName).emotion,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [hour]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  // Dynamic subtitle based on daily progress
  const dynamicSubtitle = useMemo(() => {
    const remaining = dailyGoal - todayQuestionsAnswered;
    if (todayQuestionsAnswered >= dailyGoal) {
      return 'Je dagdoel is gehaald! 🎉';
    } else if (todayQuestionsAnswered > 0) {
      return `Nog ${remaining} ${remaining === 1 ? 'vraag' : 'vragen'} voor je dagdoel!`;
    } else {
      return `Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`;
    }
  }, [todayQuestionsAnswered, dailyGoal, selectedGroup]);
  const recommendation = useMemo(() => {
    return getDjayRecommendation(state.familyMastery || {}, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions);
  }, [state.familyMastery, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions]);
  const recommendedModule = useMemo(() => {
    return recommendation ? modules.find(m => m.id === recommendation.moduleId) : null;
  }, [recommendation, modules]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  // Sort modules: in-progress -> new -> mastered
  const sortedModules = useMemo(() => {
    const inProgress = modules.filter(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      return (answered > 0 || mastery > 0) && mastery < 95;
    }).sort((a, b) => getModuleMasteryForGroup(a.id) - getModuleMasteryForGroup(b.id));
    const newModules = modules.filter(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      return answered === 0 && mastery === 0;
    });
    const mastered = modules.filter(m => getModuleMasteryForGroup(m.id) >= 95);
    return [...inProgress, ...newModules, ...mastered];
  }, [modules, getModuleMasteryForGroup, getAnsweredQuestions]);
  // Get decaying families
  const decayingFamilies = useMemo(() => {
    if (!state.familyMastery) return [];
    const weakest = getWeakestFamiliesGlobal(state.familyMastery, selectedGroup, 10);
    return weakest.filter(f => hasActiveDecay(state.familyMastery?.[f.key])).slice(0, 2);
  }, [state.familyMastery, selectedGroup]);
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Enabled modes
  const enabledModes = useMemo(() => getEnabledModes().filter(m => ['fluency', 'sprint', 'challenge'].includes(m.id)), []);
  const visibleModules = showAllModules ? sortedModules : sortedModules.slice(0, 5);
  return <PageContainer className="space-y-8 pb-20" data-id="element-1134">
      {/* HERO ZONE */}
      <motion.div variants={itemVariants} data-id="element-1135">
        <PageHero title={`${greeting}, ${displayName}!`} subtitle={dynamicSubtitle} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={djayEmotion} djayVariant={selectedVariant} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={['🚀', '⭐']} data-id="element-1136">
          <HeroStatPill icon={Star} data-id="element-1137">Level {state.stats.level}</HeroStatPill>
        </PageHero>
      </motion.div>

      {/* KLASCODE BANNER (SUBTLE) */}
      {showKlascodeBanner && <motion.div variants={itemVariants} data-id="element-1138">
          <Card className="p-3 sm:p-4 border border-amber-200 bg-amber-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden" data-id="element-1139">
            <div className="flex items-center gap-3" data-id="element-1140">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0" data-id="element-1141">
                <Users className="w-5 h-5" data-id="element-1142" />
              </div>
              <div data-id="element-1143">
                <h3 className="text-sm font-bold text-amber-900" data-id="element-1144">
                  Heb je een klascode?
                </h3>
                <p className="text-xs text-amber-700 font-medium" data-id="element-1145">
                  Je kunt deze op elk moment invullen via Instellingen.
                </p>
              </div>
            </div>
            <button onClick={handleDismissKlascodeBanner} className="absolute top-2 right-2 sm:static p-1.5 text-amber-500 hover:bg-amber-100 rounded-lg transition-colors" title="Sluiten" data-id="element-1146">
              <X className="w-4 h-4" data-id="element-1147" />
            </button>
          </Card>
        </motion.div>}

      {/* ACTIVE ASSIGNMENTS */}
      {activeAssignments.length > 0 && <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1148">
          {activeAssignments.map(assignment => {
        const teacherName = accounts.accountsById[assignment.teacherId]?.displayName || 'Je leraar';
        return <AssignedAssessmentBanner key={assignment.id} assignment={assignment} teacherName={teacherName} onStartAssessment={() => {
          const config: AssessmentConfig = {
            domainId: assignment.domainId,
            mode: assignment.type === 'targeted' ? 'targeted' : 'general',
            targetFamilyIds: assignment.selectedFamilyIds || [],
            assignmentId: assignment.id,
            questionCount: assignment.questionCount || 30,
            moduleFilter: DOMAIN_MODULE_MAP[assignment.domainId as keyof typeof DOMAIN_MODULE_MAP] || null,
            minModules: 1,
            countsForMastery: false,
            seed: Date.now()
          };
          onStartAssessment(config);
        }} data-id="element-1149" />;
      })}
        </motion.div>}

      {/* PRIMARY ACTION - MOVED UP */}
      <motion.div variants={itemVariants} data-id="element-1150">
        {hasStarted && recommendedModule && recommendation ? <DjayRecommendationCard module={recommendedModule} recommendation={recommendation} onStart={() => onStartPractice(recommendedModule.id)} djayVariant={selectedVariant} data-id="element-1151" /> : <WelcomeCard onStart={onNavigateToModules} djayVariant={selectedVariant} data-id="element-1152" />}
      </motion.div>

      {/* DAGDOEL & STREAK ROW - COMBINED ON MOBILE */}
      <motion.div variants={itemVariants} data-id="element-1153">
        {/* Mobile: single compact row */}
        <div className="md:hidden" data-id="element-1154">
          <Card className="p-4 border-2 border-amber-100 bg-white rounded-2xl" data-id="element-1155">
            <div className="flex items-center justify-between gap-4" data-id="element-1156">
              {/* Daily Goal */}
              <div className="flex items-center gap-3" data-id="element-1157">
                <div className="relative" style={{
                width: 60,
                height: 60
              }} data-id="element-1158">
                  <svg width={60} height={60} viewBox="0 0 60 60" className="transform -rotate-90" data-id="element-1159">
                    <circle cx={30} cy={30} r={25} fill="none" stroke="#FEF3C7" strokeWidth={6} data-id="element-1160" />
                    <motion.circle initial={{
                    strokeDashoffset: 157
                  }} animate={{
                    strokeDashoffset: 157 - Math.min(100, todayQuestionsAnswered / dailyGoal * 100) / 100 * 157
                  }} transition={{
                    duration: 1.5,
                    ease: 'easeOut'
                  }} cx={30} cy={30} r={25} fill="none" stroke={todayQuestionsAnswered >= dailyGoal ? '#10B981' : '#F59E0B'} strokeWidth={6} strokeDasharray={157} strokeLinecap="round" data-id="element-1161" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1162">
                    <span className="text-lg font-black text-slate-800 leading-none" data-id="element-1163">
                      {todayQuestionsAnswered}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400" data-id="element-1164">
                      /{dailyGoal}
                    </span>
                  </div>
                </div>
                <div data-id="element-1165">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-1166">
                    Dagdoel
                  </div>
                  <div className="text-sm font-bold text-amber-600" data-id="element-1167">
                    {todayQuestionsAnswered >= dailyGoal ? 'Gehaald! 🎉' : 'Goed bezig!'}
                  </div>
                </div>
              </div>

              {/* Streak */}
              <div className="flex items-center gap-3" data-id="element-1168">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center" data-id="element-1169">
                  <Flame className="w-6 h-6 text-orange-500" fill="currentColor" data-id="element-1170" />
                </div>
                <div data-id="element-1171">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-1172">
                    Streak
                  </div>
                  <div className="flex items-baseline gap-1" data-id="element-1173">
                    <span className="text-2xl font-black text-slate-800" data-id="element-1174">
                      {currentStreak}
                    </span>
                    <span className="text-xs font-bold text-slate-500" data-id="element-1175">
                      dagen
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly activity dots */}
            <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-1176">
              <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" data-id="element-1177" />
            </div>
          </Card>
        </div>

        {/* Desktop: two-column grid */}
        <div className="hidden md:grid grid-cols-2 gap-6" data-id="element-1178">
          <DailyGoalCard answered={todayQuestionsAnswered} goal={dailyGoal} data-id="element-1179" />
          <WeekStreakCard streak={currentStreak} weeklyActivity={weeklyActivity} data-id="element-1180" />
        </div>
      </motion.div>

      {/* HERHAAL TIP */}
      {decayingFamilies.length > 0 && <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1181">
          {decayingFamilies.map(f => {
        const def = getFamilyDefinition(f.familyId);
        const title = def?.description || f.familyId;
        const module = modules.find(m => f.familyId.includes(m.id));
        return <Card key={f.familyId} className="p-3 sm:p-5 border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl cursor-pointer hover:shadow-md transition-all group" onClick={() => module && onStartPractice(module.id)} data-id="element-1182">
                <div className="flex items-center gap-3 sm:gap-4" data-id="element-1183">
                  <div className="flex-shrink-0" data-id="element-1184">
                    <DjayAvatar size="xs" emotion="thinking" variant={selectedVariant} animate data-id="element-1185" />
                  </div>
                  <div className="flex-1" data-id="element-1186">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1" data-id="element-1187">
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600" data-id="element-1188" />
                      <h3 className="text-sm sm:text-base font-bold text-teal-900" data-id="element-1189">
                        Tijd voor een opfrisser!
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-teal-700 font-medium" data-id="element-1190">
                      Je hebt <strong className="text-teal-900" data-id="element-1191">{title}</strong>{' '}
                      al een tijdje niet geoefend.
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 group-hover:bg-teal-500 group-hover:text-white transition-colors flex-shrink-0" data-id="element-1192">
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1193" />
                  </div>
                </div>
              </Card>;
      })}
        </motion.div>}

      {/* JOUW VAKKEN */}
      <motion.div variants={itemVariants} className="pt-4" data-id="element-1194">
        <div className="flex items-center justify-between mb-6" data-id="element-1195">
          <div className="flex items-center gap-3" data-id="element-1196">
            <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center text-orange-600" data-id="element-1197">
              <BookOpen className="w-6 h-6" data-id="element-1198" />
            </div>
            <h2 className="text-2xl font-black text-slate-800" data-id="element-1199">Jouw Vakken</h2>
          </div>
          {!showAllModules && sortedModules.length > 5 && <Button variant="ghost" size="sm" onClick={() => setShowAllModules(true)} className="text-orange-600 hover:text-orange-700 hover:bg-orange-50" data-id="element-1200">
              Toon alles
            </Button>}
        </div>

        <div className="relative -mx-4 sm:mx-0" data-id="element-1201">
          <div className={showAllModules ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6 px-4 sm:px-0 justify-items-center' : 'flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 px-4 sm:px-0 pb-4 md:grid md:grid-cols-5 md:gap-6 md:overflow-visible md:snap-none md:pb-0 justify-items-center'} data-id="element-1202">
            {visibleModules.map((module, index) => {
            // Check if any family in this module has active decay
            const families = getFamiliesForModule(module.id);
            const hasDecay = families.some(f => {
              const key = getFamilyMasteryKey(f.familyId, selectedGroup);
              return hasActiveDecay(state.familyMastery?.[key]);
            });
            return <div key={module.id} className={showAllModules ? 'w-full flex justify-center' : 'snap-start flex-shrink-0 w-[170px] md:w-auto flex justify-center'} data-id="element-1203">
                  <WorldTile module={module} selectedGroup={selectedGroup} onClick={() => onModuleSelect(module)} getModuleMasteryForGroup={getModuleMasteryForGroup} getAnsweredQuestions={getAnsweredQuestions} index={index} hasDecay={hasDecay} data-id="element-1204" />
                </div>;
          })}
          </div>
          {!showAllModules && <div className="absolute top-0 right-0 bottom-4 w-12 bg-gradient-to-l from-[#FAFAF8] to-transparent pointer-events-none md:hidden" data-id="element-1205" />}
        </div>
      </motion.div>

      {/* OEFENLAB SNELSTART - HORIZONTAL PILLS ON MOBILE */}
      <motion.div variants={itemVariants} className="pt-4" data-id="element-1206">
        <div className="flex items-center gap-3 mb-6" data-id="element-1207">
          <div className="w-12 h-12 rounded-2xl bg-fuchsia-100 flex items-center justify-center text-fuchsia-600" data-id="element-1208">
            <FlaskConical className="w-6 h-6" data-id="element-1209" />
          </div>
          <h2 className="text-2xl font-black text-slate-800" data-id="element-1210">Oefenlab</h2>
        </div>

        {/* Mobile: horizontal scroll */}
        <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-3 -mx-4 px-4 sm:hidden" data-id="element-1211">
          {enabledModes.map(mode => {
          const Icon = getModeIcon(mode.id);
          const colorMap: Record<string, {
            gradient: string;
          }> = {
            fluency: {
              gradient: 'from-amber-400 to-orange-500'
            },
            sprint: {
              gradient: 'from-indigo-400 to-purple-500'
            },
            challenge: {
              gradient: 'from-violet-400 to-fuchsia-500'
            }
          };
          const colors = colorMap[mode.id] || {
            gradient: 'from-slate-400 to-slate-500'
          };
          return <Card key={mode.id} className={`snap-start flex-shrink-0 w-[140px] p-4 flex flex-col items-center gap-2 cursor-pointer active:scale-95 transition-transform border-2 border-transparent bg-gradient-to-br ${colors.gradient} rounded-2xl relative overflow-hidden`} onClick={onNavigateToLab} data-id="element-1212">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-1213" />
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/20 text-white relative z-10" data-id="element-1214">
                  <Icon className="w-5 h-5" data-id="element-1215" />
                </div>
                <div className="relative z-10 text-center" data-id="element-1216">
                  <h3 className="font-black text-white leading-tight text-sm" data-id="element-1217">
                    {mode.title}
                  </h3>
                  <p className="text-[10px] font-medium text-white/90 mt-0.5" data-id="element-1218">
                    {mode.subtitle}
                  </p>
                </div>
              </Card>;
        })}
        </div>

        {/* Desktop: grid */}
        <div className="hidden sm:grid grid-cols-3 gap-4" data-id="element-1219">
          {enabledModes.map(mode => {
          const Icon = getModeIcon(mode.id);
          const colorMap: Record<string, {
            gradient: string;
          }> = {
            fluency: {
              gradient: 'from-amber-400 to-orange-500'
            },
            sprint: {
              gradient: 'from-indigo-400 to-purple-500'
            },
            challenge: {
              gradient: 'from-violet-400 to-fuchsia-500'
            }
          };
          const colors = colorMap[mode.id] || {
            gradient: 'from-slate-400 to-slate-500'
          };
          return <Card key={mode.id} className={`p-5 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all group border-2 border-transparent bg-gradient-to-br ${colors.gradient} rounded-2xl relative overflow-hidden`} onClick={onNavigateToLab} data-id="element-1220">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-1221" />
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/20 text-white group-hover:scale-110 transition-transform relative z-10" data-id="element-1222">
                  <Icon className="w-6 h-6" data-id="element-1223" />
                </div>
                <div className="relative z-10" data-id="element-1224">
                  <h3 className="font-black text-white leading-tight text-lg" data-id="element-1225">
                    {mode.title}
                  </h3>
                  <p className="text-xs font-medium text-white/90 mt-0.5" data-id="element-1226">
                    {mode.subtitle}
                  </p>
                </div>
              </Card>;
        })}
        </div>
      </motion.div>
    </PageContainer>;
}
function WelcomeCard({
  onStart,
  djayVariant
}: {
  onStart: () => void;
  djayVariant: DjayVariant;
}) {
  return <Card className="p-6 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-sm relative overflow-hidden rounded-[2rem]" data-id="element-1227">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1228" />
      <div className="relative z-10 flex flex-col items-center" data-id="element-1229">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-4 sm:mb-6" data-id="element-1230">
          <div className="block sm:hidden" data-id="element-1231">
            <DjayAvatar size="md" emotion="celebrating" variant={djayVariant} animate data-id="element-1232" />
          </div>
          <div className="hidden sm:block" data-id="element-1233">
            <DjayAvatar size="lg" emotion="celebrating" variant={djayVariant} animate data-id="element-1234" />
          </div>
        </motion.div>
        <h2 className="text-xl sm:text-3xl font-black text-slate-800 mb-2 sm:mb-3 tracking-tight" data-id="element-1235">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="hidden sm:block text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1236">
          Je avontuur begint hier. Kies een wereld om te oefenen, verdien XP en
          bouw je streak op! 🚀
        </p>
        <Button onClick={onStart} variant="primary" size="lg" className="shadow-md rounded-2xl sm:h-14 sm:text-lg sm:px-8" data-id="element-1237">
          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 mr-2 fill-current" data-id="element-1238" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}
function DailyGoalCard({
  answered,
  goal
}: {
  answered: number;
  goal: number;
}) {
  const percentage = Math.min(100, Math.round(answered / goal * 100));
  const size = 100;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - percentage / 100 * circumference;
  let message = 'Begin je avontuur!';
  if (percentage >= 100) message = 'Dagdoel gehaald! 🎉';else if (percentage >= 50) message = 'Bijna daar!';else if (percentage > 0) message = 'Goed bezig!';
  return <Card className="p-6 border-2 border-amber-100 bg-white rounded-[2rem] flex flex-col items-center justify-center text-center relative overflow-hidden" data-id="element-1239">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-50 rounded-full blur-2xl" data-id="element-1240" />

      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 relative z-10" data-id="element-1241">
        Dagdoel
      </h3>

      <div className="relative mb-4" style={{
      width: size,
      height: size
    }} data-id="element-1242">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90" data-id="element-1243">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#FEF3C7" strokeWidth={strokeWidth} data-id="element-1244" />
          <motion.circle initial={{
          strokeDashoffset: circumference
        }} animate={{
          strokeDashoffset: offset
        }} transition={{
          duration: 1.5,
          ease: 'easeOut'
        }} cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={percentage >= 100 ? '#10B981' : '#F59E0B'} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeLinecap="round" data-id="element-1245" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1246">
          <span className="text-2xl font-black text-slate-800 leading-none" data-id="element-1247">
            {answered}
          </span>
          <span className="text-xs font-bold text-slate-400 border-t-2 border-slate-100 pt-0.5 mt-0.5 w-8 text-center" data-id="element-1248">
            {goal}
          </span>
        </div>
      </div>

      <p className={`font-bold ${percentage >= 100 ? 'text-emerald-600' : 'text-amber-600'} relative z-10`} data-id="element-1249">
        {message}
      </p>
    </Card>;
}
function WeekStreakCard({
  streak,
  weeklyActivity
}: {
  streak: number;
  weeklyActivity: DayActivity[];
}) {
  return <Card className="p-6 border-2 border-orange-100 bg-white rounded-[2rem] flex flex-col justify-between relative overflow-hidden" data-id="element-1250">
      <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-orange-50 rounded-full blur-2xl" data-id="element-1251" />

      <div className="flex justify-between items-start mb-6 relative z-10" data-id="element-1252">
        <div data-id="element-1253">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-1254">
            Jouw Streak
          </h3>
          <div className="flex items-baseline gap-2" data-id="element-1255">
            <span className="text-4xl font-black text-slate-800" data-id="element-1256">{streak}</span>
            <span className="text-sm font-bold text-slate-500" data-id="element-1257">dagen</span>
          </div>
        </div>
        <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center" data-id="element-1258">
          <Flame className="w-7 h-7 text-orange-500" fill="currentColor" data-id="element-1259" />
        </div>
      </div>

      <div className="relative z-10" data-id="element-1260">
        <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" data-id="element-1261" />
      </div>
    </Card>;
}